import { useEffect, useRef, useCallback } from 'react';
import { Howl } from 'howler';

// URLs for ambient sounds (using free sound URLs from freesound.org - Creative Commons licensed)
const SOUND_URLS = {
  // Night ambiance - crickets and night sounds
  night: 'https://freesound.org/data/previews/531/531947_5674468-lq.mp3',
  // Day ambiance - birds and nature
  day: 'https://freesound.org/data/previews/456/456058_5121236-lq.mp3',
  // Wolf howl
  wolfHowl: 'https://freesound.org/data/previews/416/416105_4397472-lq.mp3',
  // Suspense for voting
  suspense: 'https://freesound.org/data/previews/370/370920_5450487-lq.mp3',
  // Victory fanfare
  victory: 'https://freesound.org/data/previews/387/387232_7255534-lq.mp3',
  // Defeat sound
  defeat: 'https://freesound.org/data/previews/277/277403_4486188-lq.mp3',
};

type SoundType = keyof typeof SOUND_URLS;

interface GameSounds {
  playAmbient: (type: 'night' | 'day') => void;
  stopAmbient: () => void;
  playEffect: (type: SoundType) => void;
  setVolume: (volume: number) => void;
  isMuted: boolean;
  toggleMute: () => void;
}

export const useGameSounds = (): GameSounds => {
  const soundsRef = useRef<Record<string, Howl>>({});
  const currentAmbientRef = useRef<Howl | null>(null);
  const volumeRef = useRef(0.5);
  const mutedRef = useRef(false);

  // Initialize sounds
  useEffect(() => {
    // Create Howl instances for each sound
    Object.entries(SOUND_URLS).forEach(([key, url]) => {
      soundsRef.current[key] = new Howl({
        src: [url],
        loop: key === 'night' || key === 'day', // Ambient sounds loop
        volume: volumeRef.current,
        preload: true,
        html5: true, // Better for mobile
      });
    });

    return () => {
      // Cleanup all sounds
      Object.values(soundsRef.current).forEach(sound => {
        sound.stop();
        sound.unload();
      });
    };
  }, []);

  const playAmbient = useCallback((type: 'night' | 'day') => {
    if (mutedRef.current) return;

    // Stop current ambient if playing
    if (currentAmbientRef.current) {
      currentAmbientRef.current.fade(volumeRef.current, 0, 1000);
      setTimeout(() => {
        currentAmbientRef.current?.stop();
      }, 1000);
    }

    const sound = soundsRef.current[type];
    if (sound) {
      sound.volume(0);
      sound.play();
      sound.fade(0, volumeRef.current * 0.6, 2000); // Ambient at 60% volume
      currentAmbientRef.current = sound;
    }
  }, []);

  const stopAmbient = useCallback(() => {
    if (currentAmbientRef.current) {
      currentAmbientRef.current.fade(currentAmbientRef.current.volume(), 0, 1000);
      setTimeout(() => {
        currentAmbientRef.current?.stop();
        currentAmbientRef.current = null;
      }, 1000);
    }
  }, []);

  const playEffect = useCallback((type: SoundType) => {
    if (mutedRef.current) return;

    const sound = soundsRef.current[type];
    if (sound && type !== 'night' && type !== 'day') {
      sound.volume(volumeRef.current);
      sound.play();
    }
  }, []);

  const setVolume = useCallback((volume: number) => {
    volumeRef.current = Math.max(0, Math.min(1, volume));
    Object.values(soundsRef.current).forEach(sound => {
      if (sound === currentAmbientRef.current) {
        sound.volume(volumeRef.current * 0.6);
      }
    });
  }, []);

  const toggleMute = useCallback(() => {
    mutedRef.current = !mutedRef.current;
    if (mutedRef.current) {
      stopAmbient();
    }
  }, [stopAmbient]);

  return {
    playAmbient,
    stopAmbient,
    playEffect,
    setVolume,
    isMuted: mutedRef.current,
    toggleMute,
  };
};
